import React from 'react';
import styled from 'styled-components';
import {useHistory} from 'react-router-dom';
import {Avatar, Icon, Link, SubHeading, Text, WorkSignifier, tokensForThemeProvider} from '@forecast-it/design-system';
import {CellValue, RagState} from '../../data/reportRowModel';
import {formatDuration} from '../durationFormat';
import {TruncatedCellContent} from './TruncatedCellContent';
import {isModifiedClick} from './isModifiedClick';

const tokens = tokensForThemeProvider.sky.sys;

/** Icon glyph + DS icon-color token per RAG state, matching the icon choice `RagStatusMessage`
 * itself uses internally (RagStatusMessage.js `getIcon`) — green/checkCircle, amber/warning,
 * red/alert - but WITHOUT that component's bordered-box chrome (see `StatusCellWrapper` below).
 *
 * The fourth state maps to an explicit `null`, not to a missing key: a traffic light beside the em
 * dash would read as a judgement about the project, when the whole point of that state is that we
 * have not got one. A TOTAL record rather than a `Partial` so a state added later fails to compile
 * until someone decides here whether it has an icon - a `Partial` would let it default silently to
 * no icon, which is a judgement in itself. */
const RAG_ICON: Record<RagState, {icon: string; color: string} | null> = {
	on_track: {icon: 'checkCircle', color: 'success'},
	at_risk: {icon: 'warning', color: 'warning'},
	off_track: {icon: 'alert', color: 'error'},
	unknown: null,
};

const NotAvailable = (): JSX.Element => (
	<Text size={2} color="secondary">
		N/A
	</Text>
);

/** the design renders the task status cell as plain inline text + icon — no
 * border, no box, natural (shrink-to-fit) width. The DS `RagStatusMessage` component always draws
 * a colored bordered box around its content (`RagStatusMessageWrapper` in RagStatusMessage.js) and
 * has no compact/inline variant (RagStatusMessage.d.ts exposes only `status`/`children`/
 * `iconOnLeft`), so it does not match this design — reusing it here would mean re-overriding its
 * own chrome via CSS overrides, which is worse than composing the same DS primitives (`Icon` +
 * `Text`) the rest of this file already uses. `inline-flex` keeps icon+label on one line at their
 * natural width instead of stretching to the table cell. */
const StatusCellWrapper = styled.span`
	display: inline-flex;
	align-items: center;
	gap: ${tokens.spacing['100']};
`;

/** the design's project cell is the colored id pill (`WorkSignifier`) followed by the project
 * name. `WorkSignifier` renders only the id chip inline (name is its hover title), so the visible
 * name is a `Text` beside it. `inline-flex` keeps pill + name on one line at natural width. */
const ProjectCellWrapper = styled.span`
	display: inline-flex;
	align-items: center;
	gap: ${tokens.spacing['100']};
`;

const ProjectCell = ({
	id,
	name,
	projectKey,
	color,
	href,
}: {
	id: string;
	name: string;
	projectKey: string;
	color: string;
	href: string | null;
}): JSX.Element => {
	// Called unconditionally on every render of this component, regardless of whether `href` is
	// null, so this hook is never called conditionally.
	const history = useHistory();

	// Plain left-click routes in-app so the unsaved-changes overlay can intercept it; a modified
	// click falls through to the browser, which is how a user consciously opens a new tab. The
	// real href is what makes that fall-through work and what gives the browser its own link
	// affordances (open in new tab/window, copy address, status-bar preview).
	const handleClick = (event: React.MouseEvent<HTMLAnchorElement>): void => {
		if (isModifiedClick(event)) {
			return;
		}
		event.preventDefault();
		// Only wired to the anchor in the `href` branch below, so `href` is never null here.
		if (href !== null) {
			history.push(href);
		}
	};

	return (
		<ProjectCellWrapper>
			<WorkSignifier workId={id} name={`${projectKey} ${name}`.trim()} color={color} />
			{/* The name is the link to the project page; the id pill beside it stays inert. A
			    project with no usable id has no page to open, and falls back to the emphasised
			    static name. Both carry size 2 (14px), the table's text scale — size 1 is 16px and
			    would render this one column larger than every other cell in the row. */}
			{name ? (
				<TruncatedCellContent tooltip={name}>
					{href ? (
						<Link href={href} onClick={handleClick} size={'2'}>
							{name}
						</Link>
					) : (
						<SubHeading size={'2'}>{name}</SubHeading>
					)}
				</TruncatedCellContent>
			) : null}
		</ProjectCellWrapper>
	);
};

const PersonsCell = ({persons}: {persons: {id: string; name: string; avatarUrl?: string}[]}): JSX.Element => (
	<>
		{persons.map((person, idx) => (
			<span
				key={person.id || `${person.name}-${idx}`}
				style={{display: 'inline-flex', alignItems: 'center', gap: 4, marginRight: 8}}
			>
				<Avatar name={person.name} src={person.avatarUrl} size="s" />
				<Text size={2}>{person.name}</Text>
			</span>
		))}
	</>
);

/** A cell that wraps instead of truncating. Used for the one kind of column whose value is prose
 * rather than a label - a status note - where cutting the text off at the column edge hides the
 * only thing the column is there to say. `pre-wrap` keeps the author's own line breaks. */
const WrappedText = styled.span`
	display: block;
	white-space: pre-wrap;
	overflow-wrap: anywhere;
`;

export const CellRenderer = ({value, wrap}: {value: CellValue | undefined; wrap?: boolean}): JSX.Element => {
	if (value === undefined || value.kind === 'na') {
		return <NotAvailable />;
	}

	switch (value.kind) {
		case 'text':
			return wrap ? (
				<WrappedText>
					<Text size={2}>{value.value}</Text>
				</WrappedText>
			) : (
				<Text size={2} ellipsis tooltipOnEllipsis>
					{value.value}
				</Text>
			);
		case 'number':
			return <Text size={2}>{value.value}</Text>;
		case 'percent':
			// The unit is part of the value: the catalog declares this field a percentage, and a bare
			// number in a Progress column reads as a count.
			return <Text size={2}>{`${value.value}%`}</Text>;
		case 'date':
			return <Text size={2}>{value.value}</Text>;
		case 'duration':
			return <Text size={2}>{formatDuration(value.minutes, value.unit)}</Text>;
		case 'project':
			return (
				<ProjectCell
					id={value.id}
					name={value.name}
					projectKey={value.projectKey}
					color={value.color}
					href={value.href}
				/>
			);
		case 'status': {
			const glyph = RAG_ICON[value.rag];
			return (
				<StatusCellWrapper>
					<Text size={2}>{value.label}</Text>
					{glyph ? <Icon icon={glyph.icon} size={'s'} color={glyph.color} /> : null}
				</StatusCellWrapper>
			);
		}
		case 'persons':
			return value.persons.length === 0 ? <NotAvailable /> : <PersonsCell persons={value.persons} />;
		case 'tag':
			return value.tags.length === 0 ? (
				<NotAvailable />
			) : (
				<Text size={2}>{value.tags.map(tag => tag.name).join(', ')}</Text>
			);
		default:
			return <NotAvailable />;
	}
};
